import React, { useState, useEffect } from 'react';
import { useParams, useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../hooks/useAuth';
import { useNotifications } from '../contexts/NotificationContext';
import { userApi } from '../services/userApi';
import { groupApi } from '../services/groupApi';
import Avatar from './Avatar';
import {
  IoCreateOutline,
  IoTrashOutline,
  IoPeopleOutline,
  IoDocumentTextOutline,
  IoImageOutline,
  IoVideocamOutline,
  IoMusicalNotesOutline,
  IoShieldOutline,
  IoTimeOutline,
  IoCopyOutline,
  IoCheckmarkCircle,
  IoPauseCircle,
  IoCloseCircle,
  IoLogOutOutline,
  IoRefreshCircleOutline,
  IoWarningOutline,
  IoChatbubbleOutline,
  IoEnterOutline,
  IoKeyOutline,
  IoAlertCircleOutline
} from 'react-icons/io5';
import './AdminDashboard.css';

function UserDetail() {
  const { userId } = useParams();
  const navigate = useNavigate();
  const { user: currentUser } = useAuth();
  const [user, setUser] = useState(null);
  const [allGroups, setAllGroups] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // Modal states
  const [showEditModal, setShowEditModal] = useState(false);
  const [showGroupModal, setShowGroupModal] = useState(false);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [showResetPasswordModal, setShowResetPasswordModal] = useState(false);
  const [showStatusConfirm, setShowStatusConfirm] = useState(false);
  const [showLogoutConfirm, setShowLogoutConfirm] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [actionLoading, setActionLoading] = useState(false);

  // Form state
  const [editForm, setEditForm] = useState({
    username: '',
    email: '',
    display_name: '',
    status: 'active',
    is_admin: false,
    groups: []
  });

  // Password reset form
  const [passwordForm, setPasswordForm] = useState({
    newPassword: '',
    confirmPassword: ''
  });

  // Delete confirmation
  const [deleteUsernameInput, setDeleteUsernameInput] = useState('');

  // Pending action
  const [pendingStatus, setPendingStatus] = useState(null);

  const [copied, setCopied] = useState(false);

  // Check if viewing own account
  const isOwnAccount = currentUser && parseInt(userId) === currentUser.id;

  useEffect(() => {
    loadUser();
    loadGroups();
  }, [userId]);

  const loadUser = async () => {
    setLoading(true);
    setError(null);
    try {
      const response = await userApi.getUserWithGroups(userId);
      if (response.success) {
        setUser(response.data);
        setEditForm({
          username: response.data.username || '',
          email: response.data.email || '',
          display_name: response.data.display_name || '',
          status: response.data.status || 'active',
          is_admin: response.data.is_admin || false,
          groups: response.data.groups || []
        });
      } else {
        setError('User not found');
      }
    } catch (err) {
      console.error('Failed to load user:', err);
      setError('Failed to load user');
    } finally {
      setLoading(false);
    }
  };

  const loadGroups = async () => {
    try {
      const response = await groupApi.getGroups();
      if (response.success) {
        setAllGroups(response.data || []);
      }
    } catch (err) {
      console.error('Failed to load groups:', err);
    }
  };

  const handleCopyUserId = () => {
    navigator.clipboard.writeText(user?.id?.toString() || '');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSaveEdit = async () => {
    setSaving(true);
    try {
      // API call would go here
      // await userApi.updateUser(userId, editForm);
      setShowEditModal(false);
      loadUser();
    } catch (err) {
      console.error('Failed to save user:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleSaveGroups = async () => {
    setSaving(true);
    try {
      // API call would go here
      setShowGroupModal(false);
      loadUser();
    } catch (err) {
      console.error('Failed to save groups:', err);
    } finally {
      setSaving(false);
    }
  };

  const handleStatusChange = async (newStatus) => {
    setPendingStatus(newStatus);
    setShowStatusConfirm(true);
  };

  const confirmStatusChange = async () => {
    if (!pendingStatus) return;
    setActionLoading(true);
    try {
      if (pendingStatus === 'active') {
        await userApi.activateUser(userId);
        alert('User activated successfully');
      } else if (pendingStatus === 'suspended') {
        await userApi.suspendUser(userId);
        alert('User suspended successfully');
      }
      setShowStatusConfirm(false);
      loadUser();
    } catch (err) {
      console.error('Failed to change status:', err);
      alert(err.response?.data?.error || 'Failed to change status');
    } finally {
      setActionLoading(false);
      setPendingStatus(null);
    }
  };

  const handleResetPassword = () => {
    setShowResetPasswordModal(true);
  };

  const confirmResetPassword = async () => {
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      alert('Passwords do not match');
      return;
    }
    if (passwordForm.newPassword.length < 6) {
      alert('Password must be at least 6 characters');
      return;
    }
    setActionLoading(true);
    try {
      await userApi.resetPassword(userId, { new_password: passwordForm.newPassword });
      alert('Password reset successfully');
      setShowResetPasswordModal(false);
      setPasswordForm({ newPassword: '', confirmPassword: '' });
    } catch (err) {
      console.error('Failed to reset password:', err);
      alert(err.response?.data?.error || 'Failed to reset password');
    } finally {
      setActionLoading(false);
    }
  };

  const handleForceLogout = () => {
    setShowLogoutConfirm(true);
  };

  const confirmForceLogout = async () => {
    setActionLoading(true);
    try {
      await userApi.forceLogout(userId);
      alert('User logged out successfully');
      setShowLogoutConfirm(false);
    } catch (err) {
      console.error('Failed to logout user:', err);
      alert(err.response?.data?.error || 'Failed to logout user');
    } finally {
      setActionLoading(false);
    }
  };

  const handleDelete = async () => {
    if (deleteUsernameInput !== user?.username) {
      alert('Username does not match');
      return;
    }
    setDeleting(true);
    try {
      await userApi.deleteUser(userId);
      alert('User deleted successfully');
      navigate('/admin/users');
    } catch (err) {
      console.error('Failed to delete user:', err);
      alert(err.response?.data?.error || 'Failed to delete user');
    } finally {
      setDeleting(false);
      setShowDeleteConfirm(false);
    }
  };

  const getStatusBadge = (status) => {
    const statusMap = {
      active: { class: 'active', label: 'Active', icon: IoCheckmarkCircle },
      suspended: { class: 'suspended', label: 'Suspended', icon: IoPauseCircle },
      disabled: { class: 'disabled', label: 'Disabled', icon: IoCloseCircle }
    };
    const s = statusMap[status] || statusMap.active;
    const Icon = s.icon;
    return (
      <span className={`admin-status-badge ${s.class}`}>
        <Icon /> {s.label}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="admin-user-detail-loading">
        <div className="admin-spinner"></div>
        <span>Loading user...</span>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-user-detail-error">
        <div className="admin-error-icon">!</div>
        <h2>{error}</h2>
        <p>The user you're looking for doesn't exist or has been removed.</p>
        <button onClick={() => navigate('/admin/users')} className="admin-btn primary">
          Back to Users
        </button>
      </div>
    );
  }

  return (
    <div className="admin-user-detail">
      {/* Breadcrumb */}
      <div className="admin-breadcrumb">
        <Link to="/admin">Admin</Link>
        <span className="admin-breadcrumb-sep">/</span>
        <Link to="/admin/users">Users</Link>
        <span className="admin-breadcrumb-sep">/</span>
        <span className="admin-breadcrumb-current">{user?.username}</span>
      </div>

      <div className="admin-user-detail-layout">
        {/* Left Column */}
        <div className="admin-user-detail-main">
          {/* Profile Header Card */}
          <div className="admin-profile-card">
            <div className="admin-profile-header">
              <Avatar user={user} size="xlarge" />
              <div className="admin-profile-info">
                <h1>{user?.display_name || user?.username}</h1>
                <span className="admin-profile-username">@{user?.username}</span>
                <div className="admin-profile-badges">
                  {getStatusBadge(user?.status || 'active')}
                  {user?.is_admin && <span className="admin-status-badge admin">Admin</span>}
                </div>
              </div>
            </div>
            <div className="admin-profile-meta">
              <div className="admin-meta-item">
                <label>User ID</label>
                <span>
                  {user?.id}
                  <button className="admin-copy-btn" onClick={handleCopyUserId} title="Copy User ID">
                    {copied ? <IoCheckmarkCircle /> : <IoCopyOutline />}
                  </button>
                </span>
              </div>
              <div className="admin-meta-item">
                <label>Created</label>
                <span>{user?.created_at || '-'}</span>
              </div>
              <div className="admin-meta-item">
                <label>Last Login</label>
                <span>{user?.last_login || 'Never'}</span>
              </div>
            </div>
          </div>

          {/* Profile Information */}
          <section className="admin-detail-section">
            <div className="admin-section-header">
              <h3><IoDocumentTextOutline /> Profile Information</h3>
              <button className="admin-btn small" onClick={() => setShowEditModal(true)}>
                <IoCreateOutline /> Edit
              </button>
            </div>
            <div className="admin-detail-grid">
              <div className="admin-detail-item">
                <label>Username</label>
                <span>{user?.username}</span>
              </div>
              <div className="admin-detail-item">
                <label>Display Name</label>
                <span>{user?.display_name || '-'}</span>
              </div>
              <div className="admin-detail-item">
                <label>Email</label>
                <span>{user?.email}</span>
              </div>
              <div className="admin-detail-item">
                <label>Account Status</label>
                {getStatusBadge(user?.status || 'active')}
              </div>
            </div>
          </section>

          {/* Groups */}
          <section className="admin-detail-section">
            <div className="admin-section-header">
              <h3><IoShieldOutline /> Groups</h3>
              <button className="admin-btn small" onClick={() => setShowGroupModal(true)}>
                <IoCreateOutline /> Edit Groups
              </button>
            </div>
            <div className="admin-user-groups">
              {user?.groups && user.groups.length > 0 ? (
                user.groups.map((group, idx) => (
                  <span key={idx} className="admin-group-tag large">{group}</span>
                ))
              ) : (
                <span className="admin-no-groups">No groups assigned</span>
              )}
            </div>
          </section>

          {/* Activity Summary */}
          <section className="admin-detail-section">
            <h3><IoPeopleOutline /> Activity Summary</h3>
            <div className="admin-activity-cards">
              <div className="admin-activity-card">
                <IoDocumentTextOutline className="admin-activity-card-icon" />
                <span className="admin-activity-card-value">{user?.stats?.posts || 0}</span>
                <span className="admin-activity-card-label">Posts</span>
              </div>
              <div className="admin-activity-card">
                <IoChatbubbleOutline className="admin-activity-card-icon" />
                <span className="admin-activity-card-value">{user?.stats?.messages || 0}</span>
                <span className="admin-activity-card-label">Messages</span>
              </div>
              <div className="admin-activity-card">
                <IoImageOutline className="admin-activity-card-icon" />
                <span className="admin-activity-card-value">{user?.stats?.images || 0}</span>
                <span className="admin-activity-card-label">Images</span>
              </div>
              <div className="admin-activity-card">
                <IoVideocamOutline className="admin-activity-card-icon" />
                <span className="admin-activity-card-value">{user?.stats?.videos || 0}</span>
                <span className="admin-activity-card-label">Videos</span>
              </div>
              <div className="admin-activity-card">
                <IoMusicalNotesOutline className="admin-activity-card-icon" />
                <span className="admin-activity-card-value">{user?.stats?.audios || 0}</span>
                <span className="admin-activity-card-label">Audios</span>
              </div>
            </div>
          </section>

          {/* Recent Activity */}
          <section className="admin-detail-section">
            <h3><IoTimeOutline /> Recent Activity</h3>
            <div className="admin-activity-list">
              {user?.recent_activity && user.recent_activity.length > 0 ? (
                user.recent_activity.map((activity, idx) => (
                  <div key={idx} className="admin-activity-item">
                    <div className="admin-activity-item-icon">
                      {activity.type === 'post' && <IoDocumentTextOutline />}
                      {activity.type === 'message' && <IoChatbubbleOutline />}
                      {activity.type === 'login' && <IoEnterOutline />}
                      {activity.type === 'upload' && <IoImageOutline />}
                    </div>
                    <div className="admin-activity-item-content">
                      <span className="admin-activity-item-text">{activity.description}</span>
                      <span className="admin-activity-item-time">{activity.timestamp}</span>
                    </div>
                  </div>
                ))
              ) : (
                <div className="admin-no-activity">No recent activity</div>
              )}
            </div>
          </section>
        </div>

        {/* Right Column - Quick Actions */}
        <div className="admin-user-detail-sidebar">
          <div className="admin-quick-actions-card">
            <h3>Quick Actions</h3>
            <div className="admin-quick-actions">
              {user?.status === 'active' ? (
                <button
                  className="admin-quick-action-btn warning"
                  onClick={() => handleStatusChange('suspended')}
                  disabled={actionLoading}
                >
                  <IoPauseCircle /> Suspend User
                </button>
              ) : (
                <button
                  className="admin-quick-action-btn success"
                  onClick={() => handleStatusChange('active')}
                  disabled={actionLoading}
                >
                  <IoCheckmarkCircle /> Activate User
                </button>
              )}
              <button
                className="admin-quick-action-btn"
                onClick={handleResetPassword}
                disabled={actionLoading}
              >
                <IoKeyOutline /> Reset Password
              </button>
              <button
                className="admin-quick-action-btn"
                onClick={handleForceLogout}
                disabled={actionLoading}
              >
                <IoLogOutOutline /> Force Logout
              </button>
              <button
                className={`admin-quick-action-btn danger ${isOwnAccount ? 'disabled' : ''}`}
                onClick={() => !isOwnAccount && setShowDeleteConfirm(true)}
                disabled={isOwnAccount || actionLoading}
                title={isOwnAccount ? "You cannot delete your own account" : "Delete User"}
              >
                <IoTrashOutline /> Delete User
              </button>
              {isOwnAccount && (
                <span className="admin-self-delete-warning">
                  <IoWarningOutline /> You cannot delete your own account
                </span>
              )}
            </div>
          </div>
        </div>
      </div>

      {/* Edit User Modal */}
      {showEditModal && (
        <div className="admin-modal-overlay" onClick={() => setShowEditModal(false)}>
          <div className="admin-modal admin-modal-large" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Edit User</h3>
              <button className="admin-modal-close" onClick={() => setShowEditModal(false)}>×</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-edit-field">
                <label>Username</label>
                <input
                  type="text"
                  value={editForm.username}
                  onChange={(e) => setEditForm({...editForm, username: e.target.value})}
                />
              </div>
              <div className="admin-edit-field">
                <label>Display Name</label>
                <input
                  type="text"
                  value={editForm.display_name}
                  onChange={(e) => setEditForm({...editForm, display_name: e.target.value})}
                />
              </div>
              <div className="admin-edit-field">
                <label>Email</label>
                <input
                  type="email"
                  value={editForm.email}
                  onChange={(e) => setEditForm({...editForm, email: e.target.value})}
                />
              </div>
              <div className="admin-edit-field">
                <label>Account Status</label>
                <select
                  value={editForm.status}
                  onChange={(e) => setEditForm({...editForm, status: e.target.value})}
                >
                  <option value="active">Active</option>
                  <option value="suspended">Suspended</option>
                  <option value="disabled">Disabled</option>
                </select>
              </div>
              <div className="admin-edit-field">
                <label>
                  <input
                    type="checkbox"
                    checked={editForm.is_admin}
                    onChange={(e) => setEditForm({...editForm, is_admin: e.target.checked})}
                  />
                  Admin Status
                </label>
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn secondary" onClick={() => setShowEditModal(false)}>Cancel</button>
              <button className="admin-btn primary" onClick={handleSaveEdit} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Group Management Modal */}
      {showGroupModal && (
        <div className="admin-modal-overlay" onClick={() => setShowGroupModal(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>Manage Group Membership</h3>
              <button className="admin-modal-close" onClick={() => setShowGroupModal(false)}>×</button>
            </div>
            <div className="admin-modal-body">
              <div className="admin-group-list">
                {allGroups.map(group => (
                  <label key={group.id} className="admin-group-checkbox">
                    <input
                      type="checkbox"
                      checked={editForm.groups?.includes(group.name)}
                      onChange={(e) => {
                        const groups = editForm.groups || [];
                        if (e.target.checked) {
                          setEditForm({...editForm, groups: [...groups, group.name]});
                        } else {
                          setEditForm({...editForm, groups: groups.filter(g => g !== group.name)});
                        }
                      }}
                    />
                    <span>{group.name}</span>
                    {group.is_system && <span className="admin-system-badge">System</span>}
                  </label>
                ))}
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn secondary" onClick={() => setShowGroupModal(false)}>Cancel</button>
              <button className="admin-btn primary" onClick={handleSaveGroups} disabled={saving}>
                {saving ? 'Saving...' : 'Save Changes'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Status Change Confirmation Modal */}
      {showStatusConfirm && (
        <div className="admin-modal-overlay" onClick={() => setShowStatusConfirm(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3>{pendingStatus === 'active' ? 'Activate User' : 'Suspend User'}</h3>
              <button className="admin-modal-close" onClick={() => setShowStatusConfirm(false)}>×</button>
            </div>
            <div className="admin-modal-body">
              <p>
                {pendingStatus === 'active'
                  ? `Are you sure you want to activate ${user?.username}?`
                  : `Are you sure you want to suspend ${user?.username}?`}
              </p>
              {pendingStatus === 'suspended' && (
                <p className="admin-delete-warning">The user will not be able to log in until reactivated.</p>
              )}
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn secondary" onClick={() => setShowStatusConfirm(false)} disabled={actionLoading}>Cancel</button>
              <button className="admin-btn primary" onClick={confirmStatusChange} disabled={actionLoading}>
                {actionLoading ? 'Processing...' : (pendingStatus === 'active' ? 'Activate' : 'Suspend')}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Reset Password Modal */}
      {showResetPasswordModal && (
        <div className="admin-modal-overlay" onClick={() => setShowResetPasswordModal(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3><IoKeyOutline /> Reset Password</h3>
              <button className="admin-modal-close" onClick={() => setShowResetPasswordModal(false)}>×</button>
            </div>
            <div className="admin-modal-body">
              <p>Enter a new password for <strong>{user?.username}</strong></p>
              <div className="admin-edit-field">
                <label>New Password</label>
                <input
                  type="password"
                  value={passwordForm.newPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, newPassword: e.target.value})}
                  placeholder="Enter new password"
                />
              </div>
              <div className="admin-edit-field">
                <label>Confirm Password</label>
                <input
                  type="password"
                  value={passwordForm.confirmPassword}
                  onChange={(e) => setPasswordForm({...passwordForm, confirmPassword: e.target.value})}
                  placeholder="Confirm new password"
                />
              </div>
              {passwordForm.newPassword && passwordForm.confirmPassword && passwordForm.newPassword !== passwordForm.confirmPassword && (
                <p className="admin-delete-warning">Passwords do not match</p>
              )}
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn secondary" onClick={() => setShowResetPasswordModal(false)} disabled={actionLoading}>Cancel</button>
              <button
                className="admin-btn primary"
                onClick={confirmResetPassword}
                disabled={actionLoading || !passwordForm.newPassword || !passwordForm.confirmPassword}
              >
                {actionLoading ? 'Resetting...' : 'Reset Password'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Force Logout Confirmation Modal */}
      {showLogoutConfirm && (
        <div className="admin-modal-overlay" onClick={() => setShowLogoutConfirm(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3><IoLogOutOutline /> Force Logout</h3>
              <button className="admin-modal-close" onClick={() => setShowLogoutConfirm(false)}>×</button>
            </div>
            <div className="admin-modal-body">
              <p>Are you sure you want to force logout <strong>{user?.username}</strong>?</p>
              <p className="admin-delete-warning">The user will be logged out from all devices.</p>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn secondary" onClick={() => setShowLogoutConfirm(false)} disabled={actionLoading}>Cancel</button>
              <button className="admin-btn warning" onClick={confirmForceLogout} disabled={actionLoading}>
                {actionLoading ? 'Logging out...' : 'Force Logout'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {showDeleteConfirm && (
        <div className="admin-modal-overlay" onClick={() => setShowDeleteConfirm(false)}>
          <div className="admin-modal" onClick={e => e.stopPropagation()}>
            <div className="admin-modal-header">
              <h3><IoAlertCircleOutline /> Delete User</h3>
              <button className="admin-modal-close" onClick={() => setShowDeleteConfirm(false)}>×</button>
            </div>
            <div className="admin-modal-body">
              <p>Are you sure you want to delete this user?</p>
              <p className="admin-delete-warning">This action cannot be undone.</p>
              <div className="admin-edit-field">
                <label>Type username to confirm: <strong>{user?.username}</strong></label>
                <input
                  type="text"
                  value={deleteUsernameInput}
                  onChange={(e) => setDeleteUsernameInput(e.target.value)}
                  placeholder={`Type ${user?.username}`}
                />
              </div>
            </div>
            <div className="admin-modal-footer">
              <button className="admin-btn secondary" onClick={() => setShowDeleteConfirm(false)} disabled={deleting}>Cancel</button>
              <button
                className="admin-btn danger"
                onClick={handleDelete}
                disabled={deleting || deleteUsernameInput !== user?.username}
              >
                {deleting ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default UserDetail;